import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

interface MediaEntry {
  key: string
  contactId: string
  messageId: string
  bytes: ArrayBuffer
  mime: string
  at: number
}

interface MediaDb extends DBSchema {
  blobs: {
    key: string
    value: MediaEntry
  }
}

const ram = new Map<string, Uint8Array>()

function cacheKey(contactId: string, messageId: string): string {
  return `${contactId}:${messageId}`
}

let dbPromise: Promise<IDBPDatabase<MediaDb>> | null = null

function getDb(): Promise<IDBPDatabase<MediaDb>> {
  if (!dbPromise) {
    dbPromise = openDB<MediaDb>('zion-media-cache', 1, {
      upgrade(db) {
        db.createObjectStore('blobs', { keyPath: 'key' })
      },
    })
  }
  return dbPromise
}

export async function putMediaBytes(
  contactId: string,
  messageId: string,
  bytes: Uint8Array,
  mime: string,
): Promise<void> {
  const key = cacheKey(contactId, messageId)
  ram.set(key, bytes)
  const db = await getDb()
  await db.put('blobs', {
    key,
    contactId,
    messageId,
    bytes: bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
    mime,
    at: Date.now(),
  })
}

export async function getMediaBytes(contactId: string, messageId: string): Promise<Uint8Array | null> {
  const key = cacheKey(contactId, messageId)
  const hit = ram.get(key)
  if (hit) return hit
  const db = await getDb()
  const row = await db.get('blobs', key)
  if (!row) return null
  const bytes = new Uint8Array(row.bytes)
  ram.set(key, bytes)
  return bytes
}

export async function deleteMediaBytes(contactId: string, messageId: string): Promise<void> {
  const key = cacheKey(contactId, messageId)
  ram.delete(key)
  const db = await getDb()
  await db.delete('blobs', key)
}

export async function clearAllMediaCache(): Promise<void> {
  ram.clear()
  const db = await getDb()
  await db.clear('blobs')
}

export function bytesToObjectUrl(bytes: Uint8Array, mime: string): string {
  const copy = new Uint8Array(bytes)
  const blob = new Blob([copy], { type: mime || 'application/octet-stream' })
  return URL.createObjectURL(blob)
}

/** iOS/Safari WebKit — blob: per immagini/GIF spesso bloccati; preferire data: URI. */
export function isIosSafari(): boolean {
  const ua = navigator.userAgent
  const ios =
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const webkit = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)
  return ios && webkit
}

export function bytesToDataUrl(bytes: Uint8Array, mime: string): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return `data:${mime || 'application/octet-stream'};base64,${btoa(binary)}`
}

/** URL per <img>: su iOS/Safari usa data-URI per GIF/sticker/immagini decifrate. */
export function bytesToDisplayUrl(bytes: Uint8Array, mime: string): string {
  const type = (mime || 'application/octet-stream').toLowerCase()
  if (isIosSafari() && type.startsWith('image/')) {
    return bytesToDataUrl(bytes, type)
  }
  return bytesToObjectUrl(bytes, mime)
}

export function triggerDownload(bytes: Uint8Array, mime: string, fileName: string): void {
  const url = bytesToObjectUrl(bytes, mime)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName || 'zion-file'
  a.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 5000)
}
